import Popover from '@mui/material/Popover';
import { FC, useMemo } from 'react';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import { Theme, useTheme } from '@mui/material';
import startCase from 'lodash/startCase';
import { useTranslation } from 'react-i18next';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { Iconify } from '@mgm/common/components';
import { MemoizedBaseAvatar } from '@mgm/common/components/base';
import { dateFormatTZ, convertToCamelCase, isSameOrAfter } from '@mgm/common/utils';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { getSelectedScheduleShift } from '../../../../store/schedules/selectors';
import { ReactComponent as TrashIcon } from '../../../../static/icons/ic_trash.svg';
import { ReactComponent as EditIcon } from '../../../../static/icons/ic_edit.svg';
import { ReactComponent as ShiftIcon } from '../../../../static/icons/ic_today.svg';
import { ReactComponent as ClockIcon } from '../../../../static/icons/ic_clock.svg';
import { ReactComponent as ApproveIcon } from '../../../../static/icons/ic_approved.svg';
import { ReactComponent as DeclineIcon } from '../../../../static/icons/ic_decline.svg';
import { ReactComponent as CommentIcon } from '../../../../static/icons/ic_comment.svg';
import { ReactComponent as BreakIcon } from '../../../../static/icons/ic_break.svg';
import { commonActions } from '../../../../store/common/slice';
import { schedulesActions } from '../../../../store/schedules/slice';
import { TShiftPopover } from './type';
import { useApproveTimeOffAPI, useDeclineTimeOffAPI } from '../../../../hooks/api/useSchedules';
import { TScheduleShift, TTimeOff } from '../../../../types';
import { usePermissions } from '../../../../hooks/useCommon';
import { statusColorMap } from '../../Calendar/data/calendar';
import { getMembers } from '../../../../store/team/selectors';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';
import { useSchedulePermissions } from '../hooks';

const ShiftPopover: FC<TShiftPopover> = ({ anchorEl, setAnchorEl }) => {
  const selectedScheduleShift = useAppSelector(getSelectedScheduleShift) as TTimeOff;
  const { personnel } = useAppSelector(getAuthenticateUser);
  const { schedulePermissions } = useSchedulePermissions();
  const { t } = useTranslation('schedule');

  const dispatch = useAppDispatch();
  const theme = useTheme() as TCustomTheme;

  const { isMobile } = useCustomTheme(theme);
  const { mutateAsync: approveMutate } = useApproveTimeOffAPI();
  const { mutateAsync: declineMutate } = useDeclineTimeOffAPI();
  const { permissions } = usePermissions();
  const members = useAppSelector(getMembers);

  const open = Boolean(anchorEl);

  const statusMap = statusColorMap(theme as Theme);

  const handleClose = () => {
    setAnchorEl(null);
    dispatch(schedulesActions.setSelectedScheduleShift({} as TScheduleShift));
  };

  const openEditNav = () => {
    dispatch(commonActions.setIsOpenPagesNav(true));
    setAnchorEl(null);
  };

  const openDeleteDialog = () => {
    dispatch(schedulesActions.setIsOpenDeleteShiftDialog(true));
    setAnchorEl(null);
  };

  const openDeleteTimeoff = () => {
    dispatch(schedulesActions.setIsOpenDeleteTimeOffDialog(true));
    setAnchorEl(null);
  };

  const getMainData = useMemo(() => {
    const date = dateFormatTZ(selectedScheduleShift.start, 'MMM Do YYYY', selectedScheduleShift.timezone);
    const start = dateFormatTZ(selectedScheduleShift.start, 'h:mm A', selectedScheduleShift.timezone);
    const end = dateFormatTZ(selectedScheduleShift.end, 'h:mm A', selectedScheduleShift.timezone);

    return {
      date,
      title: selectedScheduleShift?.type ? t(`${convertToCamelCase(selectedScheduleShift.type)}`) : `${start} - ${end}`,
      userPermission: members.find((member) => member.email === selectedScheduleShift.email)?.permissionLevel,
    };
  }, [selectedScheduleShift]);

  const handleApprove = async () => {
    const data = {
      timeoffId: selectedScheduleShift.id,
      comment: '',
    };

    await approveMutate(data);
    setAnchorEl(null);
  };

  const openNotesNav = () => {
    dispatch(schedulesActions.setIsOpenShiftNotesNav(true));
    setAnchorEl(null);
  };

  const handleDecline = async () => {
    const data = {
      timeoffId: selectedScheduleShift.id,
      comment: '',
    };
    await declineMutate(data);
    setAnchorEl(null);
  };

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      open={open}
      onClose={handleClose}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
    >
      <Card sx={{ width: isMobile ? 'auto' : 450 }}>
        {!selectedScheduleShift.answered ? (
          <CardHeader
            sx={{
              p: 2,
            }}
            title={selectedScheduleShift.type ? t('viewTimeoff') : t('viewShift')}
            {...((isSameOrAfter(selectedScheduleShift.start)
              || selectedScheduleShift.type
            ) && {
              action: (
                <Stack>
                  {!selectedScheduleShift?.type ? (
                    <Stack marginTop={1} direction="row" spacing={2}>
                      <Box
                        sx={{
                          cursor: 'pointer',
                        }}
                        onClick={openNotesNav}
                      >
                        <Iconify
                          sx={{
                            color: '#10B981',
                          }}
                          width={24}
                          icon="proicons:note-add"
                        />
                      </Box>
                      {schedulePermissions.isAllowManageTimeoff && (
                        <>
                          <Box onClick={openEditNav}>
                            <SvgIcon
                              className="cursor-pointer"
                              component={EditIcon}
                              inheritViewBox
                            />
                          </Box>
                          <Box onClick={openDeleteDialog}>
                            <SvgIcon className="cursor-pointer" component={TrashIcon} inheritViewBox />
                          </Box>
                        </>
                      )}
                    </Stack>
                  ) : (
                    <Box marginTop={1}>
                      {!selectedScheduleShift.answered && !permissions.isEmployee
                      && (
                        <Stack direction="row" spacing={1}>
                          <Box onClick={handleApprove}>
                            <SvgIcon
                              className="cursor-pointer"
                              sx={{
                                mt: '1px',
                              }}
                              color="success"
                              component={ApproveIcon}
                              inheritViewBox
                            />
                          </Box>
                          <Box onClick={handleDecline}>
                            <SvgIcon
                              className="cursor-pointer"
                              sx={{
                                fontSize: 27,
                              }}
                              component={DeclineIcon}
                              inheritViewBox
                            />
                          </Box>
                        </Stack>
                      )}
                    </Box>
                  )}
                </Stack>
              ),
            })}
          />
        ) : ((permissions.isAdmin && getMainData.userPermission !== 'Admin')
          || (permissions.isAdmin && personnel.email === selectedScheduleShift.email)) && (
          <CardHeader
            sx={{
              p: 2,
            }}
            title={t('viewTimeoff')}
            action={(
              <Box mt={1} onClick={openDeleteTimeoff}>
                <SvgIcon className="cursor-pointer" component={TrashIcon} inheritViewBox />
              </Box>
          )}
          />
        )}
        <Divider sx={{ borderStyle: 'dashed' }} />
        <CardContent sx={{
          '&:last-child': { pb: 1 },
        }}
        >
          <Box alignItems="center" display="flex">
            <Box>
              <SvgIcon
                sx={{
                  mr: 2,
                }}
                color="primary"
                component={ShiftIcon}
                inheritViewBox
              />
            </Box>
            <div className="align-center">
              <MemoizedBaseAvatar
                size={{
                  width: 22,
                  height: 22,
                }}
                avatarName={selectedScheduleShift.name}
                avatarFile={selectedScheduleShift.avatarFile}
              />
              <Typography>{startCase(selectedScheduleShift.name)}</Typography>
            </div>
          </Box>
          <Divider sx={{ pt: 2, borderStyle: 'dashed' }} />
          <Box alignItems="center" display="flex">
            <Box>
              <SvgIcon
                sx={{
                  mr: 3,
                }}
                component={ClockIcon}
                inheritViewBox
              />
            </Box>
            <div className="align-center">
              <Typography
                {...(isMobile && {
                  fontSize: 13,
                })}
                variant="body2"
              >
                {getMainData.date}
              </Typography>
              <Typography
                fontWeight={700}
                fontSize={24}
                mx={2}
                mb={2}
                variant="body2"
              >
                .
              </Typography>
              <Typography
                {...(isMobile && {
                  fontSize: 13,
                })}
                variant="body2"
              >
                {getMainData.title}
              </Typography>
              {selectedScheduleShift.status && (
                <>
                  <Typography
                    fontWeight={700}
                    fontSize={24}
                    mx={2}
                    mb={2}
                    variant="body2"
                  >
                    .
                  </Typography>
                  <Typography
                    pl={1}
                    color={statusMap[selectedScheduleShift.status]}
                  >
                    {selectedScheduleShift.status}
                  </Typography>
                </>
              )}
            </div>
          </Box>
          {selectedScheduleShift.answered && (
            <>
              <Divider sx={{ mb: 2, borderStyle: 'dashed' }} />
              <Box alignItems="center" pb={2} display="flex">
                <SvgIcon
                  sx={{
                    mr: 3,
                  }}
                  component={CommentIcon}
                  inheritViewBox
                />
                <Typography variant="body2">{selectedScheduleShift?.note || ''}</Typography>
              </Box>
            </>
          )}
          {selectedScheduleShift?.breaks && selectedScheduleShift.breaks?.length > 0 && (
            <>
              <Divider sx={{ borderStyle: 'dashed' }} />
              <Stack py={2} spacing={1}>
                {selectedScheduleShift.breaks?.map((breakData) => (
                  <Box
                    key={breakData.id}
                    display="flex"
                    alignItems="center"
                  >
                    <SvgIcon
                      sx={{
                        mr: 3,
                      }}
                      component={BreakIcon}
                      inheritViewBox
                    />
                    <Typography variant="body2">
                      {`${dateFormatTZ(
                        breakData.breakStart,
                        'h:mm A',
                        selectedScheduleShift.timezone,
                      )} - ${dateFormatTZ(
                        breakData.breakEnd,
                        'h:mm A',
                        selectedScheduleShift.timezone,
                      )}`}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Popover>
  );
};

export {
  ShiftPopover,
};
